import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Button,
} from "reactstrap";
import { useMutation, useQuery } from "@apollo/client";
import {
  CREATE_SUPPORTER,
  FETCH_LEGAL_ENTITY,
} from "../mutation/createSupporter";
import SupporterSignUpContextProvider, {
  useSupporterSignUpContext,
} from "../context/SupporterStatusContext";
import { GET_ENTITY_NAME } from "../query/formQueries";
import DatePicker from "react-datepicker";
import moment from "moment";

const industryOptions = [
  "農業",
  "林業",
  "漁業",
  "鉱業",
  "採石業",
  "砂利採取業",
  "建設業",
  "製造業",
  "電気・ガス・熱供給・水道業",
  "情報通信業",
  "運輸業",
  "郵便業",
  "卸売業",
  "小売業",
  "金融業",
  "保険業",
  "不動産業",
  "物品賃貸業",
  "学術研究",
  "専門・技術サービス業",
  "宿泊業",
  "飲食サービス業",
  "生活関連サービス業",
  "娯楽業",
  "教育",
  "学習支援業",
  "医療",
  "福祉",
  "複合サービス事業",
  "サービス業（他に分類されないもの）",
  "公務",
  "分類不能の産業",
];
const prefectureOptions = [
  "北海道",
  "青森県",
  "岩手県",
  "宮城県",
  "秋田県",
  "山形県",
  "福島県",
  "茨城県",
  "栃木県",
  "群馬県",
  "埼玉県",
  "千葉県",
  "東京都",
  "神奈川県",
  "新潟県",
  "富山県",
  "石川県",
  "福井県",
  "山梨県",
  "長野県",
  "岐阜県",
  "静岡県",
  "愛知県",
  "三重県",
  "滋賀県",
  "京都府",
  "大阪府",
  "兵庫県",
  "奈良県",
  "和歌山県",
  "鳥取県",
  "島根県",
  "岡山県",
  "広島県",
  "山口県",
  "徳島県",
  "香川県",
  "愛媛県",
  "高知県",
  "福岡県",
  "佐賀県",
  "長崎県",
  "熊本県",
  "大分県",
  "宮崎県",
  "鹿児島県",
  "沖縄県",
];

const Step0 = ({ setDisableAction }) => {
  const [openCSOAcc, setOpenCSOAcc] = useState(false);
  const [legalEntityNo, setLegalEntityNo] = useState("");

  const { state, dispatch } = useSupporterSignUpContext();

  const [formState, setFormState] = useState({
    entityNumber: "",
    entityForm: "",
    name: "",
    nameKana: "",
    representativeName: "",
    incorporationDate: "",
    prefecture: "",
    city: "",
    phoneNumber: "",
    industry: "農業",
    mission: "",
    sustainabilityPolicy: "",
    collaborationExperienceTime: "",
    collaborationNeeds: [],
    focusedSdges: [],
    focusedStakeholders: [],
  });

  const { data: dataEntity } = useQuery(GET_ENTITY_NAME);

  const formFields = useMemo(() => [
    {
      name: "entityForm",
      enLabel: "Entity Type",
      jpLabel: "法人格",
      required: true,
      type: "select",
      value: formState.entityForm,
      options: dataEntity
        ? dataEntity?.allLegalEntity?.edges?.map((item) => {
            return {
              value: item.node.title,
              label: item.node.title,
            };
          })
        : [],
    },
    {
      name: "name",
      enLabel: "Entity Name",
      jpLabel: "法人名",
      required: true,
      type: "text",
      placeholder: "例) 〇〇株式会社",
      value: formState.name,
    },
    {
      name: "nameKana",
      enLabel: "Entity Name (Kana)",
      jpLabel: "法人名(カナ)",
      required: true,
      type: "text",
      placeholder: "例）〇〇カブシキガイシャ",
      value: formState.nameKana,
    },
    {
      name: "representativeName",
      enLabel: "Representative Name",
      jpLabel: "代表者名",
      required: true,
      type: "text",
      placeholder: "例) 山田太郎",
    },
    {
      name: "incorporationDate",
      enLabel: "Establish Year",
      jpLabel: "設立年",
      required: true,
      type: "date",
      value: formState.incorporationDate,
      // placeholder: "例）山田太郎",
    },
    {
      name: "prefecture",
      enLabel: "Address",
      jpLabel: "本社住所",
      required: true,
      type: "select",
      options: prefectureOptions?.map((item) => {
        return {
          value: item,
          label: item,
        };
      }),
      placeholder: "例）2015年",
      value: formState.prefecture,
    },
    {
      name: "city",
      enLabel: "City",
      jpLabel: "市区町村",
      required: true,
      type: "text",
      placeholder: "例) 千代田区１番地11-1",
      value: formState.city,
    },
    {
      name: "phoneNumber",
      enLabel: "Phone Number",
      jpLabel: "電話番号",
      required: true,
      type: "text",
      placeholder: "例) 03-1234-5678",
    },
    {
      name: "industry",
      enLabel: "Industry",
      jpLabel: "業種",
      required: true,
      type: "select",
      options: industryOptions.map((item) => {
        return {
          value: item,
          label: item,
        };
      }),
      value: formState.industry,
      placeholder: "選択してください",
    },
  ]);

  const [createSupporter, { loading: createLoading, error: createError }] =
    useMutation(CREATE_SUPPORTER);

  const [
    mutateFetchLegakEntity,
    { loading: fetchLegalLoading, data: dataFetchLegalEntity },
  ] = useMutation(FETCH_LEGAL_ENTITY);

  const doCreateSupporter = async () => {
    try {
      await createSupporter({
        variables: {
          entityNumber: formState.entityNumber,
          entityForm: formState.entityForm,
          name: formState.name,
          nameKana: formState.nameKana,
          representativeName: formState.representativeName,
          incorporationDate: formState.incorporationDate,
          prefecture: formState.prefecture,
          city: formState.city,
          phoneNumber: formState.phoneNumber,
          industry: formState.industry,
          mission: formState.mission,
          sustainabilityPolicy: formState.sustainabilityPolicy,
          collaborationExperienceTime: formState.collaborationExperienceTime,
          collaborationNeeds: formState.collaborationNeeds,
          focusedSdges: formState.focusedSdges,
          focusedStakeholders: formState.focusedStakeholders,
        },
      });
    } catch (error) {
      alert(error);
      alert("入力内容に誤りがあります");
    }
  };

  const handleChange = (event) => {
    event.persist();

    setFormState((formState) => ({
      ...formState,
      [event.target.name]:
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value,
    }));
  };

  const onFetchLegalEntity = async () => {
    try {
      await mutateFetchLegakEntity({
        variables: {
          legalEntityNumber: legalEntityNo,
        },
      }).then((res) => {
        const data = res.data.fetchLegalEntityInfo?.legalEntityInfo;
        const decodedData = JSON.parse(data);
        const {
          entityForm,
          name,
          nameKana,
          incorporationDate,
          prefecture,
          city,
        } = decodedData;

        dispatch({
          type: "HANDLE_BULK_CHANGE",
          payload: {
            entityForm: entityForm,
            name,
            nameKana,
            incorporationDate,
            prefecture: prefecture,
            city,
          },
        });
        setFormState({
          ...formState,
          entityNumber: decodedData.entityNumber,
          entityForm: decodedData.entityForm,
          incorporationDate: decodedData.incorporationDate,
          prefecture: decodedData.prefecture,
          city: decodedData.city,
          nameKana: decodedData.nameKana,
          name: decodedData.name,
        });
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    // check if all required fields in state is filled
    const isFormValid = formFields.every((field) => {
      return field.required ? state[field.name] : true;
    });
    setDisableAction(!isFormValid);
  }, [state, formFields, setDisableAction]);
  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        const keys = formFields
          .map((field) => field.name)
          .filter((name) => name !== "incorporationDate");
        const form = event.currentTarget;
        const values = keys
          .map((key) => ({
            key: key,
            value: form[key].value,
          }))
          .reduce((acc, curr) => {
            return {
              ...acc,
              [curr.key]: curr.value,
            };
          }, {});
        dispatch({
          type: "HANDLE_BULK_CHANGE",
          payload: values,
        });
      }}
    >
      <FormGroup>
        <Row className="ema__flex_row">
          <Col xs={12} md={3}>
            法人番号検索
          </Col>
          <Col
            xs={12}
            md={5}
            style={{
              paddingRight: "5px",
            }}
          >
            <Input
              type="text"
              name="legalEntityNo"
              placeholder="法人番号"
              onChange={(e) => setLegalEntityNo(e.target.value)}
            />
          </Col>

          <div className="ml-1 hatena_box">
            <div className="hatena">？</div>
            <p className="explaine">
              次の
              <a
                style={{
                  textDecoration: "underline",
                  marginLeft: "-10px",
                  display: "contents",
                }}
                target="_blank"
                href="https://www.houjin-bangou.nta.go.jp/"
              >
                リンク
              </a>
              から法人番号を検索できます。
            </p>
          </div>
          <Col xs={12} md={3}>
            <Button
              block
              style={{
                color: "#FFFFFF",
                backgroundColor: "#44CAE1",
                padding: "12px 2px",
                borderRadius: "4px",
                border: "none",
              }}
              onClick={onFetchLegalEntity}
            >
              検索
            </Button>
          </Col>
        </Row>
      </FormGroup>
      {formFields.map((field) => {
        return (
          <>
            <FormGroup inline={true}>
              <Row>
                <Col xs={12} md={3}>
                  <Label for={field.name} className="mr-sm-2">
                    {field.jpLabel}
                    {field.required && (
                      <span
                        style={{
                          backgroundColor: "#FF6D6D",
                          color: "white",
                          padding: "2px 4px",
                          borderRadius: "2px",
                          fontSize: "9px",
                          marginLeft: "8px",
                        }}
                      >
                        必須
                      </span>
                    )}
                  </Label>
                </Col>
                <Col xs={12} md={9}>
                  {field.type === "date" && (
                    // <Input
                    //   type={field.type}
                    //   name={field.name}
                    //   id={field.name}
                    //   placeholder={field?.placeholder ?? ""}
                    //   {...(field.required && { required: true })}
                    //   defaultValue={state[field.name]}
                    // />
                    <DatePicker
                      className="form-control"
                      showMonthDropdown
                      showYearDropdown
                      placeholderText="例) 2000年01月01日"
                      defaultValue={new Date()}
                      value={state[field.name]}
                      selected={
                        state[field.name] ? new Date(state[field.name]) : null
                      }
                      dateFormat="yyyy年MM月dd日"
                      {...(field.required && { required: true })}
                      onChange={(date) => {
                        const formatted = moment(date).format("yyyy-MM-DD");
                        dispatch({
                          type: "HANDLE_CHANGE",
                          payload: {
                            name: field.name,
                            value: formatted,
                          },
                        });
                      }}
                    />
                  )}
                  {field.type === "text" && (
                    <Input
                      type={field.type}
                      name={field.name}
                      id={field.name}
                      placeholder={field?.placeholder ?? ""}
                      defaultValue={state[field.name]}
                      value={state[field.name]}
                      onChange={(e) => {
                        const { value } = e.target;
                        dispatch({
                          type: "HANDLE_CHANGE",
                          payload: {
                            name: field.name,
                            value,
                          },
                        });
                      }}
                      {...(field.required && { required: true })}
                    />
                  )}
                  {field.type === "select" && (
                    <Input
                      type="select"
                      name={field.name}
                      id={field.name}
                      {...(field.required && { required: true })}
                      defaultValue={state[field.name]}
                      onChange={(e) => {
                        const { value } = e.target;
                        dispatch({
                          type: "HANDLE_CHANGE",
                          payload: {
                            name: field.name,
                            value,
                          },
                        });
                      }}
                    >
                      {field.options.map((option) => {
                        return (
                          <option
                            selected={
                              option.value === field.value ? true : false
                            }
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </option>
                        );
                      })}
                    </Input>
                  )}
                </Col>
              </Row>
            </FormGroup>
          </>
        );
      })}
    </Form>
  );
};

export default Step0;
